import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useApi from "@/hooks/useApi";

interface Tag { id: number; name: string; color: string; }
interface Note { id: number; agentId: number | null; noteText: string; createdAt: string; }

interface ContactDetail {
  id: number;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile: string;
  company: string;
  companyWebsite: string;
  jobTitle: string;
  city: string;
  state: string;
  country: string;
  address: string;
  zipCode: string;
  language: string;
  picture: string | null;
  dateCreated: string;
  lastActivity: string | null;
  tags: Tag[];
  notes: Note[];
}

interface LeadScore {
  total: number;
  tier: string;
  breakdown: { label: string; points: number; icon: string }[];
}

interface TimelineEvent {
  type: "note" | "campaign_email" | "broadcast_email" | "conversation";
  timestamp: string | null;
  title: string;
  description: string;
  meta?: {
    opened?: boolean;
    clicked?: boolean;
    openCount?: number;
    clickCount?: number;
  } | null;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function formatShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return "Today, " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatShort(iso);
}

const AVATAR_COLORS = [
  "bg-blue-500", "bg-violet-500", "bg-emerald-500", "bg-amber-500",
  "bg-rose-500", "bg-cyan-500", "bg-indigo-500", "bg-teal-500"
];
function avatarColor(id: number): string { return AVATAR_COLORS[id % AVATAR_COLORS.length]; }
function initials(name: string): string {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex flex-col">
      <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">{label}</span>
      <span className="text-sm text-slate-700 mt-0.5">{value}</span>
    </div>
  );
}

function TimelineIcon({ type }: { type: string }) {
  const base = "w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0";
  switch (type) {
    case "note":            return <div className={`${base} bg-amber-100 text-amber-600`}>📝</div>;
    case "campaign_email":  return <div className={`${base} bg-blue-100 text-blue-600`}>📧</div>;
    case "broadcast_email": return <div className={`${base} bg-violet-100 text-violet-600`}>📣</div>;
    case "conversation":    return <div className={`${base} bg-emerald-100 text-emerald-600`}>💬</div>;
    default:                return <div className={`${base} bg-slate-100 text-slate-500`}>•</div>;
  }
}

const TIER_STYLES: Record<string, { badge: string; bar: string; label: string }> = {
  "Qualified": { badge: "bg-emerald-100 text-emerald-700 border-emerald-200", bar: "bg-emerald-500", label: "🔥 Qualified" },
  "Hot":       { badge: "bg-orange-100 text-orange-700 border-orange-200",   bar: "bg-orange-500",  label: "🔥 Hot" },
  "Warm":      { badge: "bg-amber-100 text-amber-700 border-amber-200",      bar: "bg-amber-400",   label: "☀️ Warm" },
  "Cold":      { badge: "bg-slate-100 text-slate-500 border-slate-200",      bar: "bg-slate-300",   label: "❄️ Cold" },
};

function LeadScoreBadge({ score }: { score: LeadScore }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const style = TIER_STYLES[score.tier] ?? TIER_STYLES["Cold"];
  return (
    <div className="relative">
      <button
        onClick={() => setShowBreakdown(s => !s)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-medium ${style.badge} transition-colors hover:opacity-80`}
        title="Lead Score — click to see breakdown"
      >
        <span>{style.label}</span>
        <span className="font-bold">{score.total}</span>
        <span className="font-normal text-[10px] opacity-60">/ 100</span>
      </button>
      {/* Score bar */}
      <div className="w-full bg-slate-100 rounded-full h-1 mt-1">
        <div className={`${style.bar} h-1 rounded-full transition-all`} style={{ width: `${score.total}%` }} />
      </div>
      {/* Breakdown popover */}
      {showBreakdown && score.breakdown.length > 0 && (
        <div className="absolute right-0 top-full mt-1 z-50 w-60 bg-white border border-slate-200 rounded-xl shadow-xl p-3">
          <p className="text-xs font-semibold text-slate-700 mb-2">Score breakdown</p>
          <div className="space-y-1.5">
            {score.breakdown.map((b, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-600 flex items-center gap-1">
                  <span>{b.icon}</span>{b.label}
                </span>
                <span className={`text-xs font-bold ${b.points >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                  {b.points >= 0 ? "+" : ""}{b.points}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-2 pt-2 flex justify-between">
            <span className="text-xs text-slate-500 font-medium">Total score</span>
            <span className="text-xs font-bold text-slate-900">{score.total} / 100</span>
          </div>
        </div>
      )}
    </div>
  );
}

function EngagementPills({ meta }: { meta: TimelineEvent["meta"] }) {
  if (!meta) return null;
  return (
    <div className="flex gap-1.5 mt-1.5">
      {meta.opened && (
        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
          Opened {meta.openCount && meta.openCount > 1 ? `×${meta.openCount}` : ""}
        </span>
      )}
      {meta.clicked && (
        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
          Clicked {meta.clickCount && meta.clickCount > 1 ? `×${meta.clickCount}` : ""}
        </span>
      )}
      {!meta.opened && !meta.clicked && (
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Not opened</span>
      )}
    </div>
  );
}

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"notes" | "timeline">("timeline");
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [leadScore, setLeadScore] = useState<LeadScore | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [contactRes, scoreRes] = await Promise.all([
          api.admin.get(`Contacts/${id}`),
          api.admin.get(`LeadScore/${id}`).catch(() => null),
        ]);
        if (!contactRes.ok) { setError("Contact not found"); return; }
        setContact(await contactRes.json());
        if (scoreRes?.ok) setLeadScore(await scoreRes.json());
      } catch {
        setError("Failed to load contact.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, api.admin]);

  useEffect(() => {
    if (activeTab !== "timeline" || !id) return;
    async function loadTimeline() {
      setTimelineLoading(true);
      try {
        const res = await api.admin.get(`Contacts/${id}/Timeline`);
        if (res.ok) setTimeline(await res.json());
      } catch {}
      finally { setTimelineLoading(false); }
    }
    loadTimeline();
  }, [activeTab, id, api.admin]);

  async function addNote() {
    if (!noteText.trim() || !contact) return;
    setAddingNote(true);
    try {
      const resp = await api.admin.post(`Contacts/${id}/Notes`, { noteText: noteText.trim() });
      if (resp.ok) {
        const note = await resp.json();
        setContact((prev) => prev ? { ...prev, notes: [note, ...prev.notes] } : prev);
        // Also refresh timeline if open
        if (activeTab === "timeline") {
          setTimeline(prev => [{ type: "note", timestamp: note.createdAt, title: "Note added", description: note.noteText, meta: null }, ...prev]);
        }
        setNoteText("");
      }
    } finally {
      setAddingNote(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        Loading contact…
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3">
        <p className="text-slate-500">{error ?? "Contact not found."}</p>
        <button
          onClick={() => navigate("/contact_center/contacts")}
          className="text-sm text-blue-600 underline"
        >
          Back to contacts
        </button>
      </div>
    );
  }

  const location = [contact.city, contact.state, contact.country].filter(Boolean).join(", ");

  return (
    <div className="max-w-5xl">
      {/* Back */}
      <button
        onClick={() => navigate("/contact_center/contacts")}
        className="text-sm text-slate-500 hover:text-slate-800 mb-4 flex items-center gap-1"
      >
        ← Back to contacts
      </button>

      {/* Profile header */}
      <div className="flex items-start gap-4 mb-6 p-4 bg-white border border-slate-200 rounded-lg">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-semibold shrink-0 ${avatarColor(contact.id)}`}
        >
          {contact.picture ? (
            <img src={contact.picture} alt={contact.fullName} className="w-14 h-14 rounded-full object-cover" />
          ) : (
            initials(contact.fullName || contact.email || "?")
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-slate-900">
            {contact.fullName || <span className="text-slate-400 italic">No name</span>}
          </h1>
          {contact.jobTitle && contact.company && (
            <p className="text-sm text-slate-500 mt-0.5">
              {contact.jobTitle} · {contact.company}
            </p>
          )}
          {(contact.jobTitle && !contact.company) && (
            <p className="text-sm text-slate-500 mt-0.5">{contact.jobTitle}</p>
          )}
          {(!contact.jobTitle && contact.company) && (
            <p className="text-sm text-slate-500 mt-0.5">{contact.company}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="hover:text-blue-600 underline">
                {contact.email}
              </a>
            )}
            {contact.phone && <span>{contact.phone}</span>}
            {location && <span>{location}</span>}
          </div>
        </div>
        <div className="text-right text-xs text-slate-400 shrink-0 space-y-2">
          <div>Added {formatShort(contact.dateCreated)}</div>
          {contact.lastActivity && (
            <div>Active {formatShort(contact.lastActivity)}</div>
          )}
          {leadScore && (
            <div className="mt-2">
              <LeadScoreBadge score={leadScore} />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4">
        {/* Left: contact fields */}
        <div className="col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Contact Info
            </h3>
            <div className="space-y-3">
              <Field label="Email" value={contact.email} />
              <Field label="Phone" value={contact.phone} />
              <Field label="Mobile" value={contact.mobile} />
              <Field label="Company" value={contact.company} />
              {contact.companyWebsite && (
                <div className="flex flex-col">
                  <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">Website</span>
                  <a
                    href={contact.companyWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline mt-0.5 truncate"
                  >
                    {contact.companyWebsite}
                  </a>
                </div>
              )}
              <Field label="Job Title" value={contact.jobTitle} />
              <Field label="Address" value={contact.address} />
              <Field label="City" value={contact.city} />
              <Field label="State" value={contact.state} />
              <Field label="Zip Code" value={contact.zipCode} />
              <Field label="Country" value={contact.country} />
              <Field label="Language" value={contact.language} />
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Tags</h3>
            {contact.tags.length === 0 ? (
              <p className="text-xs text-slate-400">No tags</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {contact.tags.map((t) => (
                  <span
                    key={t.id}
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: t.color ? `${t.color}22` : "#e2e8f0",
                      color: t.color ?? "#475569"
                    }}
                  >
                    {t.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: notes + timeline */}
        <div className="col-span-3">
          <div className="bg-white border border-slate-200 rounded-lg h-full flex flex-col">
            {/* Tab header */}
            <div className="flex border-b border-slate-200 px-4 pt-2">
              <button
                onClick={() => setActiveTab("timeline")}
                className={`pb-2 px-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "timeline" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              >
                Activity Timeline
                {timeline.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{timeline.length}</span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("notes")}
                className={`pb-2 px-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "notes" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              >
                Notes
                {contact.notes.length > 0 && (
                  <span className="ml-1.5 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{contact.notes.length}</span>
                )}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* ── Notes tab ── */}
              {activeTab === "notes" && (
                <>
                  {/* Add note */}
                  <div className="mb-4">
                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note about this contact…"
                      rows={3}
                      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-blue-500 placeholder:text-slate-400"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote();
                      }}
                    />
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-slate-400">⌘ + Enter to save</span>
                      <button
                        onClick={addNote}
                        disabled={!noteText.trim() || addingNote}
                        className="text-xs px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors"
                      >
                        {addingNote ? "Saving…" : "Add Note"}
                      </button>
                    </div>
                  </div>

                  {/* Note list */}
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {contact.notes.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-8">No notes yet</p>
                    ) : (
                      contact.notes.map((n) => (
                        <div key={n.id} className="border border-slate-100 rounded-lg px-3 py-2.5 bg-slate-50">
                          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{n.noteText}</p>
                          <p className="text-xs text-slate-400 mt-1.5">{formatDate(n.createdAt)}</p>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {/* ── Timeline tab ── */}
              {activeTab === "timeline" && (
                <>
                  {/* Quick note from timeline tab too */}
                  <div className="mb-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Log a note…"
                        className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 placeholder:text-slate-400"
                        onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
                      />
                      <button
                        onClick={addNote}
                        disabled={!noteText.trim() || addingNote}
                        className="px-3 py-2 bg-[var(--color-primary)] text-white text-xs rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors whitespace-nowrap"
                      >
                        {addingNote ? "…" : "Log Note"}
                      </button>
                    </div>
                  </div>

                  {timelineLoading ? (
                    <div className="text-sm text-slate-400 text-center py-8">Loading activity…</div>
                  ) : timeline.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="text-3xl mb-2">📋</div>
                      <p className="text-slate-500 font-medium">No activity yet</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Notes, emails, and conversations with this contact will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Vertical timeline line */}
                      <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-100" />
                      <div className="space-y-4 pl-1">
                        {timeline.map((event, i) => (
                          <div key={i} className="flex gap-3 relative">
                            <TimelineIcon type={event.type} />
                            <div className="flex-1 min-w-0 pb-1">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-slate-800 leading-snug">{event.title}</p>
                                <span className="text-xs text-slate-400 shrink-0 mt-0.5">
                                  {formatRelative(event.timestamp)}
                                </span>
                              </div>
                              {event.description && (
                                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed line-clamp-2">
                                  {event.description}
                                </p>
                              )}
                              {event.meta && <EngagementPills meta={event.meta} />}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
